import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { getModuleBySlug } from "../../api/subjects";
import ModuleContainer from "../module-container/ModuleContainer";
import Module from "./module/Module";

import "./EmbedModuleRuntimePage.css";

const normalize = (value) => String(value ?? "").trim();

function getSectionSlug(section) {
  return normalize(section?.sectionSlug || section?.slug);
}

function getQuestionSectionSlug(question) {
  return normalize(question?.sectionSlug);
}

function getQuestionId(question) {
  return normalize(question?.id);
}

function getEmbedModule(moduleData, search) {
  const params = new URLSearchParams(search);
  const questionId = normalize(params.get("q"));
  const sectionSlug = normalize(params.get("section"));
  const indexParam = normalize(params.get("index"));
  const questions = Array.isArray(moduleData?.questions) ? moduleData.questions : [];
  const sections = Array.isArray(moduleData?.sections) ? moduleData.sections : [];

  let selectedQuestion = null;
  let selectedIndex = -1;

  if (questionId) {
    selectedIndex = questions.findIndex((question) => getQuestionId(question) === questionId);
    selectedQuestion = selectedIndex >= 0 ? questions[selectedIndex] : null;
  }

  if (!selectedQuestion && indexParam) {
    const index = Number(indexParam);
    if (Number.isInteger(index) && index >= 0 && index < questions.length) {
      selectedIndex = index;
      selectedQuestion = questions[index];
    }
  }

  if (!selectedQuestion && sectionSlug) {
    selectedIndex = questions.findIndex((question) => getQuestionSectionSlug(question) === sectionSlug);
    selectedQuestion = selectedIndex >= 0 ? questions[selectedIndex] : null;
  }

  const selectedSectionSlug = getQuestionSectionSlug(selectedQuestion) || sectionSlug;
  const selectedSection = selectedSectionSlug
    ? sections.find((section) => getSectionSlug(section) === selectedSectionSlug)
    : null;
  const selectedQuestions =
    selectedQuestion && selectedSectionSlug
      ? questions
          .map((question, index) => ({ question, index }))
          .filter(
            ({ question, index }) =>
              index >= selectedIndex && getQuestionSectionSlug(question) === selectedSectionSlug
          )
          .map(({ question }) => question)
      : selectedQuestion
        ? questions.slice(selectedIndex)
        : questions;

  return {
    title: selectedSection?.title
      ? `${moduleData?.title || ""}: ${selectedSection.title}`
      : moduleData?.title || "",
    questions: selectedQuestions,
    pdfPath: moduleData?.pdfPath || null,
    pdfFileName: moduleData?.pdfFileName || null,
  };
}

const EmbedModuleRuntimePage = () => {
  const { id, subject, author, moduleSlug } = useParams();
  const location = useLocation();
  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!id || !subject || !author || !moduleSlug) return;
      setLoading(true);
      try {
        const data = await getModuleBySlug(id, subject, author, moduleSlug);
        if (!mounted) return;
        setModuleData({
          title: data?.title || "",
          sections: Array.isArray(data?.sections) ? data.sections : [],
          questions: Array.isArray(data?.questions) ? data.questions : [],
          pdfPath: data?.pdfPath || null,
          pdfFileName: data?.pdfFileName || null,
        });
      } catch (e) {
        if (mounted) setModuleData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [id, subject, author, moduleSlug]);

  const embedModule = useMemo(
    () => (moduleData ? getEmbedModule(moduleData, location.search) : null),
    [moduleData, location.search]
  );

  if (loading) {
    return (
      <Box className="module-embed-page module-embed-state">
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!embedModule) {
    return (
      <Box className="module-embed-page module-embed-state">
        <Typography variant="body2" color="text.secondary">
          Завдання не знайдено
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="module-embed-page">
      <ModuleContainer component={Module} module={embedModule} />
    </Box>
  );
};

export default EmbedModuleRuntimePage;
